/**
 * Hooks for Velociraptor integration
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import type {
  VelociraptorStatus,
  VqlArtefact,
  BuiltPackage,
  CollectorBuildResult,
  BuildCollectorInput,
  BuildFromTemplateInput,
  ListArtefactsParams,
} from '../types/velociraptor'
import {
  getHealth,
  listArtefacts,
  getArtefact,
  groupArtefactsByCategory,
  filterArtefacts,
  getSuggestedArtefacts,
} from '../services/velociraptor'
import { useBuildActions } from './use-collector-build/use-build-actions'

// =============================================================================
// useVelociraptorHealth - Server health/connection status
// =============================================================================

interface UseVelociraptorHealthResult {
  status: VelociraptorStatus | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to check Velociraptor server health
 */
export function useVelociraptorHealth(): UseVelociraptorHealthResult {
  const [status, setStatus] = useState<VelociraptorStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await getHealth()
      setStatus(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to check Velociraptor health')
      setStatus(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { status, loading, error, refetch: fetch }
}

// =============================================================================
// useVelociraptorArtefacts - List and search artefacts
// =============================================================================

interface UseVelociraptorArtefactsResult {
  artefacts: VqlArtefact[]
  artefactsByCategory: Record<string, VqlArtefact[]>
  total: number
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  search: (term: string) => VqlArtefact[]
  getSuggested: (deviceType: string) => VqlArtefact[]
}

/**
 * Hook to fetch and manage Velociraptor artefacts
 */
export function useVelociraptorArtefacts(
  params?: ListArtefactsParams
): UseVelociraptorArtefactsResult {
  const [artefacts, setArtefacts] = useState<VqlArtefact[]>([])
  const [artefactsByCategory, setArtefactsByCategory] = useState<Record<string, VqlArtefact[]>>({})
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Destructure primitive fields from `params` so the dep array below depends on
  // stable scalar values, not on the object reference. Callers commonly pass a
  // fresh object literal (`{ page, perPage }`) every render — including the
  // object itself in the dep array recreates `fetch` on every render, which
  // in turn re-fires the `useEffect([fetch])` below, triggering an infinite
  // fetch loop that exhausts the browser's HTTP socket pool
  // (net::ERR_INSUFFICIENT_RESOURCES).
  const category = params?.category
  const searchTerm = params?.search
  const builtInOnly = params?.builtInOnly

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      // Pass `params` through (not a reconstructed literal) to preserve the
      // shape's distinction between "absent" and "explicit undefined" under
      // exactOptionalPropertyTypes. Primitive fields are in the dep array
      // below so the closure captures fresh `params` whenever any field
      // actually changes.
      const data = await listArtefacts(params)
      setArtefacts(data.artefacts)
      setArtefactsByCategory(groupArtefactsByCategory(data.artefacts))
      setTotal(data.total)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch artefacts')
      setArtefacts([])
      setArtefactsByCategory({})
      setTotal(0)
    } finally {
      setLoading(false)
    }
    // Intentionally omits `params` (unstable object reference); we depend on
    // each primitive field instead. See the comment block above the destructuring.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, searchTerm, builtInOnly])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const search = useCallback(
    (term: string): VqlArtefact[] => {
      return filterArtefacts(artefacts, term)
    },
    [artefacts]
  )

  const getSuggested = useCallback(
    (deviceType: string): VqlArtefact[] => {
      return getSuggestedArtefacts(artefacts, deviceType)
    },
    [artefacts]
  )

  return {
    artefacts,
    artefactsByCategory,
    total,
    loading,
    error,
    refetch: fetch,
    search,
    getSuggested,
  }
}

// =============================================================================
// useVelociraptorArtefact - Single artefact details
// =============================================================================

interface UseVelociraptorArtefactResult {
  artefact: VqlArtefact | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch a single artefact by name
 */
export function useVelociraptorArtefact(name: string | null): UseVelociraptorArtefactResult {
  const [artefact, setArtefact] = useState<VqlArtefact | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!name) {
      setArtefact(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getArtefact(name)
      setArtefact(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch artefact')
      setArtefact(null)
    } finally {
      setLoading(false)
    }
  }, [name])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { artefact, loading, error, refetch: fetch }
}

// =============================================================================
// useCollectorBuild - Build collector packages
// =============================================================================

interface UseCollectorBuildResult {
  building: boolean
  buildResult: CollectorBuildResult | null
  buildStatus: BuiltPackage | null
  error: string | null
  build: (input: BuildCollectorInput) => Promise<CollectorBuildResult | null>
  buildFromRequest: (input: BuildFromTemplateInput) => Promise<CollectorBuildResult | null>
  checkStatus: (buildId: string) => Promise<BuiltPackage | null>
  pollUntilComplete: (
    buildId: string,
    onProgress?: (status: BuiltPackage) => void
  ) => Promise<BuiltPackage | null>
  clearError: () => void
}

/**
 * Hook for building collector packages
 */
export function useCollectorBuild(): UseCollectorBuildResult {
  const [building, setBuilding] = useState(false)
  const [buildResult, setBuildResult] = useState<CollectorBuildResult | null>(null)
  const [buildStatus, setBuildStatus] = useState<BuiltPackage | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<boolean>(false)

  const actions = useBuildActions({
    setBuilding,
    setBuildResult,
    setBuildStatus,
    setError,
    abortRef,
  })

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  useEffect(
    () => () => {
      abortRef.current = true
    },
    []
  )

  return { building, buildResult, buildStatus, error, ...actions, clearError }
}

// =============================================================================
// useArtefactSelection - Manage artefact selection for collector building
// =============================================================================

interface ArtefactSelection {
  name: string
  parameters: Record<string, string | number | boolean>
}

interface UseArtefactSelectionResult {
  selected: ArtefactSelection[]
  add: (name: string, parameters?: Record<string, string | number | boolean>) => void
  remove: (name: string) => void
  updateParameters: (name: string, parameters: Record<string, string | number | boolean>) => void
  clear: () => void
  isSelected: (name: string) => boolean
  getSelection: (name: string) => ArtefactSelection | undefined
}

/**
 * Hook for managing artefact selection state
 */
export function useArtefactSelection(): UseArtefactSelectionResult {
  const [selected, setSelected] = useState<ArtefactSelection[]>([])

  const add = useCallback(
    (name: string, parameters?: Record<string, string | number | boolean>) => {
      setSelected((prev) => {
        if (prev.some((sel) => sel.name === name)) {
          return prev
        }
        return [...prev, { name, parameters: parameters ?? {} }]
      })
    },
    []
  )

  const remove = useCallback((name: string) => {
    setSelected((prev) => prev.filter((sel) => sel.name !== name))
  }, [])

  const updateParameters = useCallback(
    (name: string, parameters: Record<string, string | number | boolean>) => {
      setSelected((prev) => prev.map((sel) => (sel.name === name ? { ...sel, parameters } : sel)))
    },
    []
  )

  const clear = useCallback(() => {
    setSelected([])
  }, [])

  const isSelected = useCallback(
    (name: string): boolean => {
      return selected.some((sel) => sel.name === name)
    },
    [selected]
  )

  const getSelection = useCallback(
    (name: string): ArtefactSelection | undefined => {
      return selected.find((sel) => sel.name === name)
    },
    [selected]
  )

  return {
    selected,
    add,
    remove,
    updateParameters,
    clear,
    isSelected,
    getSelection,
  }
}
